import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useLocation } from "react-router-dom";
import { RingLoader } from "react-spinners";
import {
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Box
} from "@mui/material";
import { formatReadableAmount } from "../../../Common/FormatFunctions/FormatAmount";
import { FormaDateBalanceSheet } from "../../../Common/FormatFunctions/FormatDate";

const apikey = process.env.REACT_APP_API;

const SaleRegisterTally = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const Company_Name = sessionStorage.getItem("Company_Name");
  const Company_GSTNO = sessionStorage.getItem("Company_GSTNO");

  const fromDate = searchParams.get("fromDate");
  const toDate = searchParams.get("toDate");
  const company_Code = searchParams.get("companyCode");
  const YearCode = searchParams.get("yearCode");
  const acCode = searchParams.get("acCode");

  const API_URL = `${apikey}/Sale_Register`;

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 0 = Detailed, 1 = Monthly, 2 = Partywise, 3 = Top Buyers
  const [tab, setTab] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    global: "",
    doc_no: "",
    billtoname: "",
    billtogstno: "",
    gstrate: "",
    amountMin: "",
    amountMax: "",
    qtyMin: "",
    qtyMax: "",
  });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    return `${day}/${month}/${year}`;
  };

  const normalize = (v) =>
    (v ?? "")
      .toString()
      .toLowerCase()
      .trim();

  const inRange = (val, min, max) => {
    const num = Number(val) || 0;
    if (min !== "" && num < Number(min)) return false;
    if (max !== "" && num > Number(max)) return false;
    return true;
  };

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(API_URL, {
          params: {
            from_date: fromDate,
            to_date: toDate,
            Company_Code: company_Code,
            Year_code: YearCode,
            acCode: acCode,
          },
        });
        setReportData(response.data || []);
      } catch (err) {
        console.error("Error fetching sale register:", err);
        setError("Error fetching report");
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL, fromDate, toDate, company_Code, YearCode, acCode]);

  // Dropdown options
  const customerOptions = useMemo(() => {
    const set = new Set(reportData.map((r) => r.billtoname).filter(Boolean));
    return Array.from(set).sort();
  }, [reportData]);

  const gstOptions = useMemo(() => {
    const set = new Set(reportData.map((r) => r.gstrate).filter((v) => v !== null && v !== undefined && v !== ""));
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [reportData]);

  // Apply filters
  const filteredRows = useMemo(() => {
    const g = normalize(filters.global);

    return reportData.filter((r) => {
      const docNoStr = normalize(r.doc_no);
      const partyStr = normalize(r.billtoname);
      const gstNoStr = normalize(r.billtogstno);
      const gstRateStr = normalize(r.gstrate);

      // Global search
      if (g) {
        const hay = `${r.doc_no ?? ""} ${r.billtoname ?? ""} ${r.billtogstno ?? ""} ${r.gstrate ?? ""}`.toLowerCase();
        if (!hay.includes(g)) return false;
      }

      if (filters.doc_no && !docNoStr.includes(normalize(filters.doc_no))) return false;
      if (filters.billtoname && !partyStr.includes(normalize(filters.billtoname))) return false;
      if (filters.billtogstno && !gstNoStr.includes(normalize(filters.billtogstno))) return false;

      if (filters.gstrate && gstRateStr !== normalize(filters.gstrate)) return false;

      if (!inRange(r.Bill_Amount, filters.amountMin, filters.amountMax)) return false;
      if (!inRange(r.NETQNTL, filters.qtyMin, filters.qtyMax)) return false;

      return true;
    });
  }, [reportData, filters]);

  // Grand totals on filtered
  const grandTotals = useMemo(() => {
    return filteredRows.reduce(
      (acc, item) => {
        acc.TotalTaxable_Amt += Number(item.TaxableAmount) || 0;
        acc.CGSTAmt += Number(item.CGSTAmount) || 0;
        acc.SGSTAmt += Number(item.SGSTAmount) || 0;
        acc.IGSTAmt += Number(item.IGSTAmount) || 0;
        acc.BillamountAmt += Number(item.Bill_Amount) || 0;
        acc.netqntl += Number(item.NETQNTL) || 0;
        return acc;
      },
      { TotalTaxable_Amt: 0, CGSTAmt: 0, SGSTAmt: 0, IGSTAmt: 0, BillamountAmt: 0, netqntl: 0 }
    );
  }, [filteredRows]);

  // ---------- Aggregations ----------

  const monthlyRows = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const d = new Date(r.doc_date);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

      const curr = map.get(key) || {
        month: key,
        netqntl: 0,
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        bill: 0,
        count: 0,
      };

      curr.netqntl += Number(r.NETQNTL) || 0;
      curr.taxable += Number(r.TaxableAmount) || 0;
      curr.cgst += Number(r.CGSTAmount) || 0;
      curr.sgst += Number(r.SGSTAmount) || 0;
      curr.igst += Number(r.IGSTAmount) || 0;
      curr.bill += Number(r.Bill_Amount) || 0;
      curr.count += 1;

      map.set(key, curr);
    });

    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredRows]);

  const partywiseRows = useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = r.billtoname || "Unknown";
      const curr = map.get(key) || {
        billtoname: key,
        billtogstno: r.billtogstno || "",
        netqntl: 0,
        taxable: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        bill: 0,
        count: 0,
      };

      if (!curr.billtogstno && r.billtogstno) curr.billtogstno = r.billtogstno;

      curr.netqntl += Number(r.NETQNTL) || 0;
      curr.taxable += Number(r.TaxableAmount) || 0;
      curr.cgst += Number(r.CGSTAmount) || 0;
      curr.sgst += Number(r.SGSTAmount) || 0;
      curr.igst += Number(r.IGSTAmount) || 0;
      curr.bill += Number(r.Bill_Amount) || 0;
      curr.count += 1;

      map.set(key, curr);
    });

    return Array.from(map.values()).sort((a, b) => (b.bill || 0) - (a.bill || 0));
  }, [filteredRows]);

  const topBuyersRows = useMemo(() => {
    return partywiseRows.slice(0, 10);
  }, [partywiseRows]);

  // ---------- Export / Print ----------

  // const handleExportToExcel = () => {
  //   const wb = XLSX.utils.book_new();

  //   if (tab === 0) {
  //     const ws = XLSX.utils.json_to_sheet(
  //       filteredRows.map((item) => ({
  //         "Bill No": item.doc_no,
  //         "Date": formatDate(item.doc_date),
  //         "Customer Name": item.billtoname,
  //         "GST No": item.billtogstno,
  //         "NetQntl": Number(item.NETQNTL) || 0,
  //         "Rate": item.gstrate,
  //         "Taxable Amount": Number(item.TaxableAmount) || 0,
  //         "CGST Amt": Number(item.CGSTAmount) || 0,
  //         "SGST Amt": Number(item.SGSTAmount) || 0,
  //         "IGST Amt": Number(item.IGSTAmount) || 0,
  //         "Bill Amount": Number(item.Bill_Amount) || 0,
  //       }))
  //     );
  //     XLSX.utils.book_append_sheet(wb, ws, "Sale_Detail");
  //   }

  //   if (tab === 1) {
  //     const ws = XLSX.utils.json_to_sheet(
  //       monthlyRows.map((m) => ({
  //         "Month": m.month,
  //         "Count": m.count,
  //         "NetQntl": m.netqntl,
  //         "Taxable": m.taxable,
  //         "CGST": m.cgst,
  //         "SGST": m.sgst,
  //         "IGST": m.igst,
  //         "Bill Amount": m.bill,
  //       }))
  //     );
  //     XLSX.utils.book_append_sheet(wb, ws, "Sale_Monthly");
  //   }

  //   if (tab === 2) {
  //     const ws = XLSX.utils.json_to_sheet(
  //       partywiseRows.map((p) => ({
  //         "Customer": p.billtoname,
  //         "GST No": p.billtogstno,
  //         "Count": p.count,
  //         "NetQntl": p.netqntl,
  //         "Taxable": p.taxable,
  //         "CGST": p.cgst,
  //         "SGST": p.sgst,
  //         "IGST": p.igst,
  //         "Bill Amount": p.bill,
  //       }))
  //     );
  //     XLSX.utils.book_append_sheet(wb, ws, "Sale_Partywise");
  //   }

  //   if (tab === 3) {
  //     const ws = XLSX.utils.json_to_sheet(
  //       topBuyersRows.map((p) => ({
  //         "Customer": p.billtoname,
  //         "GST No": p.billtogstno,
  //         "Count": p.count,
  //         "NetQntl": p.netqntl,
  //         "Bill Amount": p.bill,
  //       }))
  //     );
  //     XLSX.utils.book_append_sheet(wb, ws, "Sale_TopBuyers");
  //   }

  //   XLSX.writeFile(wb, "SaleRegister_Tally.xlsx");
  // };

  const handleExportToExcel = () => {
  const wb = XLSX.utils.book_new();
  const n = (v) => Number(v) || 0;

  if (tab === 0) {
    const rows = filteredRows.map((item) => ({
      "Bill No": item.doc_no,
      "Date": formatDate(item.doc_date),
      "Customer Name": item.billtoname,
      "GST No": item.billtogstno,
      "NetQntl": n(item.NETQNTL),
      "Rate": item.gstrate,
      "Taxable Amount": n(item.TaxableAmount),
      "CGST Amt": n(item.CGSTAmount),
      "SGST Amt": n(item.SGSTAmount),
      "IGST Amt": n(item.IGSTAmount),
      "Bill Amount": n(item.Bill_Amount),
    }));

    // ✅ Grand Total for detail (use existing grandTotals if you have; else compute here)
    const gt = filteredRows.reduce(
      (a, r) => {
        a.netqntl += n(r.NETQNTL);
        a.taxable += n(r.TaxableAmount);
        a.cgst += n(r.CGSTAmount);
        a.sgst += n(r.SGSTAmount);
        a.igst += n(r.IGSTAmount);
        a.bill += n(r.Bill_Amount);
        return a;
      },
      { netqntl: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, bill: 0 }
    );

    rows.push({
      "Bill No": "",
      "Date": "",
      "Customer Name": "Grand Total",
      "GST No": "",
      "NetQntl": gt.netqntl,
      "Rate": "",
      "Taxable Amount": gt.taxable,
      "CGST Amt": gt.cgst,
      "SGST Amt": gt.sgst,
      "IGST Amt": gt.igst,
      "Bill Amount": gt.bill,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Sale_Detail");
  }

  if (tab === 1) {
    const rows = monthlyRows.map((m) => ({
      "Month": m.month,
      "Count": n(m.count),
      "NetQntl": n(m.netqntl),
      "Taxable": n(m.taxable),
      "CGST": n(m.cgst),
      "SGST": n(m.sgst),
      "IGST": n(m.igst),
      "Bill Amount": n(m.bill),
    }));

    const mt = monthlyRows.reduce(
      (a, m) => {
        a.count += n(m.count);
        a.netqntl += n(m.netqntl);
        a.taxable += n(m.taxable);
        a.cgst += n(m.cgst);
        a.sgst += n(m.sgst);
        a.igst += n(m.igst);
        a.bill += n(m.bill);
        return a;
      },
      { count: 0, netqntl: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, bill: 0 }
    );

    rows.push({
      "Month": "Grand Total",
      "Count": mt.count,
      "NetQntl": mt.netqntl,
      "Taxable": mt.taxable,
      "CGST": mt.cgst,
      "SGST": mt.sgst,
      "IGST": mt.igst,
      "Bill Amount": mt.bill,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Sale_Monthly");
  }

  if (tab === 2) {
    const rows = partywiseRows.map((p) => ({
      "Customer": p.billtoname,
      "GST No": p.billtogstno,
      "Count": n(p.count),
      "NetQntl": n(p.netqntl),
      "Taxable": n(p.taxable),
      "CGST": n(p.cgst),
      "SGST": n(p.sgst),
      "IGST": n(p.igst),
      "Bill Amount": n(p.bill),
    }));

    const pt = partywiseRows.reduce(
      (a, p) => {
        a.count += n(p.count);
        a.netqntl += n(p.netqntl);
        a.taxable += n(p.taxable);
        a.cgst += n(p.cgst);
        a.sgst += n(p.sgst);
        a.igst += n(p.igst);
        a.bill += n(p.bill);
        return a;
      },
      { count: 0, netqntl: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, bill: 0 }
    );

    rows.push({
      "Customer": "Grand Total",
      "GST No": "",
      "Count": pt.count,
      "NetQntl": pt.netqntl,
      "Taxable": pt.taxable,
      "CGST": pt.cgst,
      "SGST": pt.sgst,
      "IGST": pt.igst,
      "Bill Amount": pt.bill,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Sale_Partywise");
  }

  if (tab === 3) {
    const rows = topBuyersRows.map((p) => ({
      "Customer": p.billtoname,
      "GST No": p.billtogstno,
      "Count": n(p.count),
      "NetQntl": n(p.netqntl),
      "Bill Amount": n(p.bill),
    }));

    const tt = topBuyersRows.reduce(
      (a, p) => {
        a.count += n(p.count);
        a.netqntl += n(p.netqntl);
        a.bill += n(p.bill);
        return a;
      },
      { count: 0, netqntl: 0, bill: 0 }
    );

    rows.push({
      "Customer": "Grand Total",
      "GST No": "",
      "Count": tt.count,
      "NetQntl": tt.netqntl,
      "Bill Amount": tt.bill,
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Sale_TopBuyers");
  }

  XLSX.writeFile(wb, "SaleRegister_Tally.xlsx");
};


  const generatePDF = async () => {
    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text(Company_Name || "Company", doc.internal.pageSize.width / 2, 10, { align: "center" });

    doc.setFontSize(9);
    doc.text(`GSTN: ${Company_GSTNO || ""}`, doc.internal.pageSize.width / 2, 16, { align: "center" });

    doc.setFontSize(11);
    const title =
      tab === 0 ? "Sale Register (Detailed)" :
      tab === 1 ? "Sale Register (Monthly)" :
      tab === 2 ? "Sale Register (Partywise)" :
      "Sale Register (Top Buyers)";

    doc.text(title, doc.internal.pageSize.width / 2, 22, { align: "center" });

    doc.setFontSize(9);
    doc.text(`${FormaDateBalanceSheet(fromDate)} to ${FormaDateBalanceSheet(toDate)}`, 10, 28);

    let head = [];
    let body = [];

    if (tab === 0) {
      head = [[
        "Bill No", "Date", "Customer", "GST No",
        "NetQntl", "Rate", "Taxable", "CGST", "SGST", "IGST", "Bill Amt"
      ]];

      body = filteredRows.map((r) => ([
        r.doc_no,
        formatDate(r.doc_date),
        r.billtoname,
        r.billtogstno,
        formatReadableAmount(r.NETQNTL),
        r.gstrate,
        formatReadableAmount(r.TaxableAmount),
        formatReadableAmount(r.CGSTAmount),
        formatReadableAmount(r.SGSTAmount),
        formatReadableAmount(r.IGSTAmount),
        formatReadableAmount(r.Bill_Amount),
      ]));

      body.push([
        "", "", "Total", "",
        formatReadableAmount(grandTotals.netqntl),
        "",
        formatReadableAmount(grandTotals.TotalTaxable_Amt),
        formatReadableAmount(grandTotals.CGSTAmt),
        formatReadableAmount(grandTotals.SGSTAmt),
        formatReadableAmount(grandTotals.IGSTAmt),
        formatReadableAmount(grandTotals.BillamountAmt),
      ]);
    }

    if (tab === 1) {
      head = [[ "Month", "Count", "NetQntl", "Taxable", "CGST", "SGST", "IGST", "Bill Amt" ]];
      body = monthlyRows.map((m) => ([
        m.month, m.count,
        formatReadableAmount(m.netqntl),
        formatReadableAmount(m.taxable),
        formatReadableAmount(m.cgst),
        formatReadableAmount(m.sgst),
        formatReadableAmount(m.igst),
        formatReadableAmount(m.bill),
      ]));
    }

    if (tab === 2) {
      head = [[ "Customer", "GST No", "Count", "NetQntl", "Taxable", "CGST", "SGST", "IGST", "Bill Amt" ]];
      body = partywiseRows.map((p) => ([
        p.billtoname,
        p.billtogstno,
        p.count,
        formatReadableAmount(p.netqntl),
        formatReadableAmount(p.taxable),
        formatReadableAmount(p.cgst),
        formatReadableAmount(p.sgst),
        formatReadableAmount(p.igst),
        formatReadableAmount(p.bill),
      ]));
    }

    if (tab === 3) {
      head = [[ "Customer", "GST No", "Count", "NetQntl", "Bill Amt" ]];
      body = topBuyersRows.map((p) => ([
        p.billtoname,
        p.billtogstno,
        p.count,
        formatReadableAmount(p.netqntl),
        formatReadableAmount(p.bill),
      ]));
    }

    doc.autoTable({
      head,
      body,
      startY: 32,
      styles: { fontSize: 7, cellPadding: 1 },
      theme: "grid",
    });

    return doc.output("blob");
  };

  const handlePrint = async () => {
    const pdfBlob = await generatePDF();
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const win = window.open(pdfUrl, "");
    win?.document?.close();
    win?.print();
  };

  // ---------- Filter handlers ----------

  const updateFilter = (key) => (e) => {
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const clearFilters = () => {
    setFilters({
      global: "",
      doc_no: "",
      billtoname: "",
      billtogstno: "",
      gstrate: "",
      amountMin: "",
      amountMax: "",
      qtyMin: "",
      qtyMax: "",
    });
  };

  // ---------- Tables ----------

  const renderDetailedTable = () => (
    <TableContainer component={Paper} style={{ maxHeight: "70vh", overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {[
              "Bill No", "Date", "Customer Name / Party Name", "GST No.",
              "Net Quintal", "Rate", "Taxable Amount", "CGST Amount",
              "SGST Amount", "IGST Amount", "Bill Amount"
            ].map((label, i) => (
              <TableCell
                key={i}
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#f5f5f5",
                  fontWeight: "bold",
                  zIndex: 2,
                  textAlign: i < 2 ? "center" : (i === 2 ? "left" : "right"),
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredRows.map((item, index) => (
            <TableRow key={index}>
              <TableCell>{item.doc_no}</TableCell>
              <TableCell>{formatDate(item.doc_date)}</TableCell>
              <TableCell style={{ textAlign: "left" }}>{item.billtoname}</TableCell>
              <TableCell>{item.billtogstno}</TableCell>
              <TableCell align="right">{formatReadableAmount(item.NETQNTL)}</TableCell>
              <TableCell align="right">{item.gstrate}</TableCell>
              <TableCell align="right">{formatReadableAmount(item.TaxableAmount)}</TableCell>
              <TableCell align="right">{formatReadableAmount(item.CGSTAmount)}</TableCell>
              <TableCell align="right">{formatReadableAmount(item.SGSTAmount)}</TableCell>
              <TableCell align="right">{formatReadableAmount(item.IGSTAmount)}</TableCell>
              <TableCell align="right">{formatReadableAmount(item.Bill_Amount)}</TableCell>
            </TableRow>
          ))}

          <TableRow>
            <TableCell
              colSpan={4}
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "yellow",
                fontWeight: "bold",
                zIndex: 1,
              }}
            >
              Total
            </TableCell>
            <TableCell
              align="right"
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "yellow",
                fontWeight: "bold",
                zIndex: 1,
              }}
            >
              {formatReadableAmount(grandTotals.netqntl || 0)}
            </TableCell>
            <TableCell
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "yellow",
                fontWeight: "bold",
                zIndex: 1,
              }}
            />
            {[grandTotals.TotalTaxable_Amt, grandTotals.CGSTAmt, grandTotals.SGSTAmt, grandTotals.IGSTAmt, grandTotals.BillamountAmt]
              .map((val, i) => (
                <TableCell
                  key={i}
                  align="right"
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "yellow",
                    fontWeight: "bold",
                    zIndex: 1,
                  }}
                >
                  {formatReadableAmount(val || 0)}
                </TableCell>
              ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderMonthlyTable = () => (
    <TableContainer component={Paper} style={{ maxHeight: "70vh", overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {["Month", "Count", "Net Quintal", "Taxable", "CGST", "SGST", "IGST", "Bill Amount"].map((h, i) => (
              <TableCell key={i} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {monthlyRows.map((m, i) => (
            <TableRow key={i}>
              <TableCell>{m.month}</TableCell>
              <TableCell>{m.count}</TableCell>
              <TableCell align="right">{formatReadableAmount(m.netqntl)}</TableCell>
              <TableCell align="right">{formatReadableAmount(m.taxable)}</TableCell>
              <TableCell align="right">{formatReadableAmount(m.cgst)}</TableCell>
              <TableCell align="right">{formatReadableAmount(m.sgst)}</TableCell>
              <TableCell align="right">{formatReadableAmount(m.igst)}</TableCell>
              <TableCell align="right">{formatReadableAmount(m.bill)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPartywiseTable = () => (
    <TableContainer component={Paper} style={{ maxHeight: "70vh", overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {["Customer", "GST No", "Count", "Net Quintal", "Taxable", "CGST", "SGST", "IGST", "Bill Amount"].map((h, i) => (
              <TableCell key={i} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {partywiseRows.map((p, i) => (
            <TableRow key={i}>
              <TableCell>{p.billtoname}</TableCell>
              <TableCell>{p.billtogstno}</TableCell>
              <TableCell>{p.count}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.netqntl)}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.taxable)}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.cgst)}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.sgst)}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.igst)}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.bill)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTopBuyersTable = () => (
    <TableContainer component={Paper} style={{ maxHeight: "70vh", overflow: "auto" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {["Customer", "GST No", "Count", "Net Quintal", "Bill Amount"].map((h, i) => (
              <TableCell key={i} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", whiteSpace: "nowrap" }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {topBuyersRows.map((p, i) => (
            <TableRow key={i}>
              <TableCell>{p.billtoname}</TableCell>
              <TableCell>{p.billtogstno}</TableCell>
              <TableCell>{p.count}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.netqntl)}</TableCell>
              <TableCell align="right">{formatReadableAmount(p.bill)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div style={{ marginTop: "-80px" }}>
      <Typography variant="h6" style={{ textAlign: "center", fontSize: "24px", fontWeight: "bold" }}>
        {Company_Name}
      </Typography>
      <Typography variant="h6" style={{ textAlign: "center", fontSize: "16px", textDecoration: "underline", fontWeight: "550" }}>
        GSTN : {Company_GSTNO}
      </Typography>
      <Typography variant="h6" style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold" }}>
        Sale Register
      </Typography>
      <Typography variant="h6" style={{ textAlign: "center", fontSize: "16px" }}>
        {FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}
      </Typography>

      {/* Buttons */}
      <div className="mb-2 row align-items-center">
        <div className="col-auto">
          <button className="btn btn-secondary me-2" onClick={handlePrint}>
            Print
          </button>
          <button className="btn btn-success me-2" onClick={handleExportToExcel}>
            Export to Excel
          </button>
          <button className="btn btn-outline-dark" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Filters */}
      <Paper style={{ padding: 12, marginBottom: 12 }}>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <TextField
            size="small"
            label="Global Search"
            value={filters.global}
            onChange={updateFilter("global")}
          />
          <TextField
            size="small"
            label="Bill No"
            value={filters.doc_no}
            onChange={updateFilter("doc_no")}
          />

          <FormControl size="small" style={{ minWidth: 220 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              label="Customer"
              value={filters.billtoname}
              onChange={updateFilter("billtoname")}
            >
              <MenuItem value="">All</MenuItem>
              {customerOptions.map((s, i) => (
                <MenuItem key={i} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="GST No"
            value={filters.billtogstno}
            onChange={updateFilter("billtogstno")}
          />

          <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel>GST Rate</InputLabel>
            <Select
              label="GST Rate"
              value={filters.gstrate}
              onChange={updateFilter("gstrate")}
            >
              <MenuItem value="">All</MenuItem>
              {gstOptions.map((g, i) => (
                <MenuItem key={i} value={String(g)}>{g}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Amount From"
            type="number"
            value={filters.amountMin}
            onChange={updateFilter("amountMin")}
          />
          <TextField
            size="small"
            label="Amount To"
            type="number"
            value={filters.amountMax}
            onChange={updateFilter("amountMax")}
          />
          <TextField
            size="small"
            label="Qty From"
            type="number"
            value={filters.qtyMin}
            onChange={updateFilter("qtyMin")}
          />
          <TextField
            size="small"
            label="Qty To"
            type="number"
            value={filters.qtyMax}
            onChange={updateFilter("qtyMax")}
          />
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper style={{ marginBottom: 10 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Detailed" />
          <Tab label="Monthly Sale" />
          <Tab label="Partywise Sale" />
          <Tab label="Top Buyers" />
        </Tabs>
      </Paper>

      {tab === 0 && renderDetailedTable()}
      {tab === 1 && renderMonthlyTable()}
      {tab === 2 && renderPartywiseTable()}
      {tab === 3 && renderTopBuyersTable()}

      {loading && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <RingLoader size={80} />
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
    </div>
  );
};

export default SaleRegisterTally;
